<template>
  <div>
    <v-row no-gutters style="gap: 0.5rem" class="align-center; mb-4">
      <v-col cols="auto" class="d-flex align-center">
        <span>{{ $i18n.t(`table.rzi${currentMetadata.type}.${data.type}.title`) }}</span>
      </v-col>
      <v-col cols="auto">
        <v-menu attach offset-y offset-x style="position: relative;">
          <template #activator="{ on }">
            <base-rounded-button
              v-on="on"
              icon="mdi-plus"
              size="x-small"
              color="success"
              @click="defaultDatasource ? openAddEditDialog() : null"
              :tooltip="$i18n.t(`table.rzi${currentMetadata.type}.${data.type}.add`)"
            />
          </template>
          <v-list v-if="!defaultDatasource" dense>
            <v-list-item
              v-for="(type, index) in Object.keys(currentMetadata.tableViews[data.type].datasources)"
              :key="index"
              @click="assignGeom(type)"
            >
              <v-list-item-title class="text-left">
                {{ $i18n.t(`table.rzi.add_${type}`) }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
      <v-col v-if="uploadData" cols="auto">
        <v-menu attach offset-y offset-x style="position: relative;">
          <template #activator="{ on }">
            <base-rounded-button
              v-on="on"
              icon="mdi-upload"
              size="x-small"
              color="primary"
              :tooltip="$i18n.t(`table.rzi${currentMetadata.type}.${data.type}.upload`)"
            />
          </template>
          <v-list dense>
            <v-list-item
              v-for="([type, dt], index) in Object.entries(currentMetadata.tableViews[data.type].datasources)"
              :key="index"
              @click="openUploadDialog(dt, type)"
            >
              <v-list-item-title class="text-left">
                {{ $i18n.t(`table.rzi.addImport_${type}`) }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" class="d-flex">
        <v-data-table class="border" dense :items-per-page="5" :headers="headers" :items="tableItems">
          <template #[`item.actions`]="{ item }">
            <v-row no-gutters style="flex-wrap: nowrap; gap: 0.2rem" class="align-center justify-end">
              <v-col v-if="item.geometry">
                <v-icon
                  size="18"
                  color="rgba(0,0,0,0.7)"
                  @click="zoomToFeature(item.geometry)"
                  @mouseenter="handleFeatureHover(item.geometry)"
                  @mouseleave="handleFeatureHover(null)"
                >
                  mdi-magnify
                </v-icon>
              </v-col>
              <v-col style="flex-grow: 0">
                <dots-menu
                  :items="getDotsMenuItems(item)"
                  @edit="openAddEditDialog(item)"
                  @addPoint="assignGeom('point', item)"
                  @removeGeometry="openAddEditDialog(item, 'geometry')"
                  @addPolygon="assignGeom('polygon', item)"
                  @delete="openAddEditDialog(item, 'item')"
                  font-size="18px"
                />
              </v-col>
            </v-row>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <add-edit-feature-dialog
      v-if="isAddEditFeatureLoaded"
      :is-visible.sync="isAddEditFeatureVisible"
      v-bind="addEditData"
      :hidden-attributes="hiddenAttributes"
      @done="handleAddEditFeature"
    >
      <template v-if="elevationGeometry" #row-input-z-append="{ changeValueTrigger }">
        <elevation-trigger :geometry="elevationGeometry" @changeValue="changeValueTrigger" />
      </template>
    </add-edit-feature-dialog>
    <main-dialog
      :v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :loading="isDeleteDialogLoading"
      :title="addEditData.title"
      :body="addEditData.body"
      @agree="handleDelete"
      agree-text="dialog.agreeDelete"
    />
    <rzi-upload-geometry-dialog
      v-if="isUploadDialogLoaded"
      :current-metadata="currentMetadata"
      :datasource="addEditData.featureData.datasource"
      :is-visible.sync="isUploadDialogVisible"
      v-bind="uploadDataProps"
      @done="handleImportGeometry"
    />
  </div>
</template>

<script>
import { get, call } from 'vuex-pathify';

import { systemAttributes } from '@/assets/js/variables';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

import DotsMenu from '@/components/DotsMenu';
import AddEditFeatureDialog from '@/components/AddEditFeatureDialog';
import RziUploadGeometryDialog from '@/components/RziUploadGeometryDialog';
import ElevationTrigger from '@/components/ElevationTrigger';
import geometryTypeMapping from '@/mixins/map/geometryTypeMapping';
import { drawing } from '@/mixins/rziHelpers';

export default {
  name: 'RziJoinedTable',
  mixins: [prepareAttributesFeatureCardMixin, geometryTypeMapping, drawing],
  components: {
    DotsMenu,
    AddEditFeatureDialog,
    RziUploadGeometryDialog,
    ElevationTrigger,
    MainDialog: () => import('@/components/MainDialog'),
  },
  props: {
    currentMetadata: {
      type: Object,
    },
    uploadData: {
      type: Boolean,
      default: false,
    },
    uploadDataProps: {
      type: Object,
    },
    data: {
      type: Object,
      required: true,
    },
    currentSite: {
      type: [Number, String],
    },
    isDrawingOn: {
      type: Boolean,
    },
    customDatasourceSchema: {
      type: Array,
      default: null,
    },
    stickyButtons: {
      type: Array,
    },
  },
  data: () => ({
    isUploadDialogLoaded: false,
    isUploadDialogVisible: false,
    isAddEditFeatureLoaded: false,
    isAddEditFeatureVisible: false,
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoading: false,
    isAddMenuOpened: false,
    drawingGeometry: null,
    editingMode: null /*** add | edit | part | delete */,
    addEditData: {
      featureData: null,
      featureMetadata: null,
    },
  }),
  computed: {
    dataSources: get('layers/metadata'),
    datasourceAttributes() {
      return this.dataSources[Object.values(this.currentMetadata.tableViews[this.data.type].datasources)[0]]
        ?.attributes_schema.attributes;
    },
    computedStickyButtons: {
      get() {
        return this.stickyButtons;
      },
      set(nV) {
        this.$emit('update:stickyButtons', nV);
      },
    },
    computedIsDrawingOn: {
      get() {
        return this.isDrawingOn;
      },
      set(nV) {
        this.$emit('update:isDrawingOn', nV);
      },
    },
    hiddenAttributes() {
      const hiddenAttributes = this.currentMetadata.tableViews[this.data.type].hiddenAttributes;
      return ['_systems', ...(hiddenAttributes ? hiddenAttributes : [])];
    },
    defaultDatasource() {
      return this.currentMetadata.tableViews[this.data.type].datasources.none ?? null;
    },
    elevationGeometry() {
      return this.addEditData?.featureMetadata?.geometry ?? this.drawingGeometry ?? null;
    },
    headers() {
      return [
        ...this.datasourceAttributes.reduce((acc, attribute) => {
          if (
            ['id', ...this.hiddenAttributes]
              .reduce((acc, item) => (acc = [...acc, ...(item === '_systems' ? systemAttributes : [item])]), [])
              .includes(attribute.name)
          )
            return acc;
          return [
            ...acc,
            {
              value: attribute.name,
              text: attribute.verbose_name,
              align: 'center',
              sortable: true,
              class: 'standard-header',
              cellClass: 'standard-cell',
            },
          ];
        }, []),
        {
          text: this.$i18n.t(`table.rzi${this.currentMetadata.type}.actions`),
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '50px',
          class: 'floating-column',
          cellClass: 'floating-column',
        },
      ];
    },
    tableItems() {
      return [...this.data.features]
        .map(ft => ({
          ...Object.entries(ft.feature.properties).reduce((acc, [key, value]) => {
            acc[key] = this.getAttributeTextValueByName(value, key, ft.datasource ?? this.defaultDatasource, true);
            return acc;
          }, {}),
          featureId: ft.feature.id,
          geometry: ft.feature.geometry,
          datasource: ft.datasource,
          type: ft.type,
        }))
        .sort((a, b) => b.featureId - a.featureId);
    },
  },
  methods: {
    moveRziFeature: call('rziData/moveRziFeature'),
    deleteDatasourceFeature: call('layers/deleteDatasourceFeature'),
    openUploadDialog(datasource, type) {
      this.addEditData = {
        featureData: {
          datasource,
          type,
        },
      };
      this.isUploadDialogVisible = this.isUploadDialogLoaded = true;
    },
    handleImportGeometry(closeDialog, data) {
      this.handleAddEditFeature(closeDialog, {
        datasource: data.datasource,
        type: this.addEditData.featureData.type,
      });
    },
    async handleAddEditFeature(closeDialog, data) {
      this.refreshLayers(
        [
          {
            name: data.datasource,
            type: data.type,
          },
        ],
        () => {
          this.handleCancelGeometry();
          closeDialog();
        }
      );
    },
    getNewItemData() {
      const filterAttribute = this.currentMetadata.tableViewsSharedData.filterAttribute;
      const statics = Object.fromEntries(
        (this.currentMetadata.tableViews[this.data.type].statics || []).map(item => [item.attribute, item.value])
      );

      return {
        datasource: this.defaultDatasource,
        staticProperties: {
          [filterAttribute]: this.currentSite,
          ...(statics ? statics : []),
        },
      };
    },
    async handleReassignGeometry(type, item, callback) {
      const datasourceToMove = this.currentMetadata.tableViews[this.data.type].datasources[type];
      await this.moveRziFeature({
        geometry: type !== 'none' ? this.drawingGeometry : null,
        fromDatasource: item.datasource,
        toDatasource: datasourceToMove,
        featureId: item.featureId,
      });
      this.refreshLayers(
        [
          {
            name: item.datasource,
            type: item.type,
          },
          {
            name: datasourceToMove,
            type: type,
          },
        ],
        callback
      );
    },
    assignGeom(type, item) {
      this.handleAssignGeometry(
        type,
        item,
        item
          ? () =>
              this.handleReassignGeometry(type, item, () => {
                this.handleCancelGeometry();
              })
          : () =>
              this.openAddEditDialog({
                datasource: this.currentMetadata.tableViews[this.data.type].datasources[type],
                staticProperties: {
                  geom: this.drawingGeometry,
                },
              })
      );
    },
    openAddEditDialog(item = {}, deleteMode) {
      const newItemData = this.getNewItemData();
      item = {
        ...newItemData,
        ...item,
        staticProperties: {
          ...(item.staticProperties || {}),
          ...newItemData.staticProperties,
        },
      };
      let featureMetadata = item.featureId
        ? [...this.data.features].find(ft => ft.feature.id === item.featureId && ft.datasource === item.datasource)
            ?.feature
        : null;
      this.addEditData = {
        featureData: {
          id: item.featureId,
          featureId: item.featureId,
          datasource: item.datasource,
          type: item.type,
          additionalInfo: {
            isRemoveGeometry: deleteMode === 'geometry' ? true : false,
          },
        },
        ...(this.customDatasourceSchema && { customDatasourceSchema: this.customDatasourceSchema }),
        title:
          deleteMode === 'item'
            ? this.$i18n.t(`table.rzi${this.currentMetadata.type}.${this.data.type}.remove`)
            : deleteMode === 'geometry'
            ? this.$i18n.t(`table.rzi${this.currentMetadata.type}.removeGeometry`)
            : this.$i18n.t(
                `table.rzi${this.currentMetadata.type}.${this.data.type}.${item.featureId ? 'edit' : 'add'}`
              ),
        body:
          deleteMode === 'item'
            ? this.$i18n.t(`table.rzi${this.currentMetadata.type}.${this.data.type}.removeBody`)
            : deleteMode === 'geometry'
            ? this.$i18n.t(`table.rzi${this.currentMetadata.type}.removeGeometryBody`)
            : '',
        featureMetadata,
        ...(item.staticProperties && {
          staticProperties: {
            ...item.staticProperties,
          },
        }),
      };
      if (deleteMode) {
        this.isDeleteDialogLoaded = this.isDeleteDialogVisible = true;
      } else {
        this.isAddEditFeatureLoaded = this.isAddEditFeatureVisible = true;
      }
    },
    async handleDelete() {
      this.isDeleteDialogLoading = true;
      try {
        if (this.addEditData.featureData.additionalInfo.isRemoveGeometry)
          return this.handleReassignGeometry('none', this.addEditData.featureData, () => {
            this.isDeleteDialogVisible = this.isDeleteDialogLoading = false;
          });
        await this.deleteDatasourceFeature({
          datasource: this.addEditData.featureData.datasource,
          id: this.addEditData.featureData.featureId,
          params: {
            cascade: true,
          },
        });
        this.refreshLayers(
          [
            {
              name: this.addEditData.featureData.datasource,
              type: this.addEditData.featureData.type,
            },
          ],
          () => {
            this.isDeleteDialogVisible = this.isDeleteDialogLoading = false;
          }
        );
      } catch {
        this.isDeleteDialogLoading = false;
      }
    },
    getDotsMenuItems(item) {
      switch (!!this.defaultDatasource) {
        case true:
          return [
            {
              name: 'edit',
            },

            ...(item.geometry
              ? [
                  {
                    name: 'removeGeometry',
                  },
                ]
              : [
                  ...(this.currentMetadata.tableViews[this.data.type].datasources.point
                    ? [
                        {
                          name: 'addPoint',
                        },
                      ]
                    : []),
                  ...(this.currentMetadata.tableViews[this.data.type].datasources.polygon
                    ? [
                        {
                          name: 'addPolygon',
                        },
                      ]
                    : []),
                ]),
            {
              name: 'delete',
            },
          ];
        case false:
          return [
            {
              name: 'edit',
            },
            {
              name: 'delete',
            },
          ];
      }
    },
  },
  beforeDestroy() {
    this.handleCancelGeometry();
  },
};
</script>

<style lang="scss" scoped>
.border {
  @include border(all, 1px);
}
:deep(table) {
  white-space: nowrap;
}
:deep(.v-data-table) {
  width: 0;
  flex-grow: 1;
}
:deep(.v-data-footer__select) {
  margin-left: 0 !important;
}
:deep(.v-data-footer) {
  justify-content: center;
}
:deep(.v-data-footer__select > div) {
  margin-top: 8px !important;
  margin-bottom: 8px !important;
}
:deep(.floating-column) {
  position: sticky !important;
  position: -webkit-sticky !important;
  right: 0;
  z-index: 1;
  background: white;
  border-left: thin solid rgba(0, 0, 0, 0.12);
}
:deep(.standard-header) {
  position: relative;
}
:deep(.standard-header i) {
  position: absolute;
}
</style>
